import i18n from 'i18next';
import { ehTipoDocumentoPais, formatarDocumentoPais } from '@malgoos/shared';
import { OCULTO, discretoAtivo, iniciais, mascararDocumento, mascararEmail, mascararTelefone, mascararUsuario, nomesOcultos } from './discreto';

const LOCALES = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' } as const;
export type Idioma = keyof typeof LOCALES;

let idiomaAtual: Idioma = 'pt';
export function definirIdiomaFormato(i: Idioma) {
  idiomaAtual = i;
}
const loc = () => LOCALES[idiomaAtual];

/** Formatadores de moeda por idioma e moeda (o `Intl.NumberFormat` é caro de criar). */
const FORMATOS_MOEDA = new Map<string, Intl.NumberFormat>();

/**
 * Formatador da moeda no idioma. narrowSymbol evita "BRL" por extenso em es; useGrouping 'always' mantém o milhar também em
 * es (4.524,00 e 35.502,80); sinal negativo sempre colado ao símbolo ("-R$ 10,00"), em qualquer idioma. Review r16 visual B9:
 * quando o símbolo estreito é só "$" (dólar americano, canadense, peso...), vale o símbolo do idioma, que diz qual é
 * ("US$ 750,00" em pt, "750,00 US$" em es, "CA$" ou o código); em en-US o "$" do próprio dólar fica, como o idioma escreve.
 */
function formatoMoeda(moeda: string) {
  const chave = `${loc()}|${moeda}`;
  const pronto = FORMATOS_MOEDA.get(chave);
  if (pronto) return pronto;
  const base = { style: 'currency', currency: moeda, useGrouping: 'always', signDisplay: 'never' } as const;
  let fmt = new Intl.NumberFormat(loc(), { ...base, currencyDisplay: 'narrowSymbol' });
  if (fmt.formatToParts(1).find((p) => p.type === 'currency')?.value === '$') fmt = new Intl.NumberFormat(loc(), { ...base, currencyDisplay: 'symbol' });
  FORMATOS_MOEDA.set(chave, fmt);
  return fmt;
}

export function dinheiro(centavos: number | null | undefined, moeda = 'BRL') {
  if (centavos === null || centavos === undefined) return '';
  const fmt = formatoMoeda(moeda);
  // Modo discreto: só o símbolo da moeda, na posição do idioma, e a marca de oculto no lugar do número (sem sinal).
  if (discretoAtivo()) return dinheiroOculto(fmt);
  const texto = fmt.format(Math.abs(centavos) / 100);
  return centavos < 0 ? `-${texto}` : texto;
}

function dinheiroOculto(fmt: Intl.NumberFormat) {
  let marcou = false;
  return fmt
    .formatToParts(1)
    .map((p) => {
      if (p.type === 'currency' || p.type === 'literal') return p.value;
      if (marcou) return '';
      marcou = true;
      return OCULTO;
    })
    .join('');
}

export function numero(v: number | null | undefined, casas = 0) {
  if (v === null || v === undefined) return '';
  return new Intl.NumberFormat(loc(), { minimumFractionDigits: casas, maximumFractionDigits: casas, useGrouping: 'always' }).format(v);
}

/** Painel de uso de IA (01/11): número grande abreviado no idioma da conta ("1,3 mi", "1.3M"), até 2 casas. */
export function compacto(v: number | null | undefined) {
  if (v === null || v === undefined) return '';
  return new Intl.NumberFormat(loc(), { notation: 'compact', maximumFractionDigits: 2 }).format(v);
}

/** Review rcrm3 visual (B4): percentual com até `casas` decimais no idioma da conta (não no do navegador), sem o "%". */
export function percentual(v: number | null | undefined, casas = 1) {
  if (v === null || v === undefined) return '';
  return new Intl.NumberFormat(loc(), { maximumFractionDigits: casas }).format(v);
}

const valida = (d: Date) => !Number.isNaN(d.getTime());
const cru = (v: string | Date) => (typeof v === 'string' ? v : '');

/** "aaaa-mm-dd" que existe no calendário (2026-02-30 e 2026-13-01 não). */
export function dataIsoValida(v: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

/** Mês de "aaaa-mm" entre 01 e 12. */
export const mesIsoValido = (v: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

/** Data no formato do idioma. Texto que não é data válida volta como veio: formatar nunca derruba a tela. */
export function data(v: string | Date | null | undefined) {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !dataIsoValida(v)) return v;
  const d = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T12:00:00`) : new Date(v);
  if (!valida(d)) return cru(v);
  return new Intl.DateTimeFormat(loc(), { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

export function dataHora(v: string | Date | null | undefined) {
  if (!v) return '';
  const d = new Date(v);
  if (!valida(d) || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && !dataIsoValida(v.slice(0, 10)))) return cru(v);
  return new Intl.DateTimeFormat(loc(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

/** "2026-10" para "10/2026" no formato do idioma. Mês fora de 01 a 12 volta como veio. */
export function mesAno(v: string | null | undefined) {
  if (!v) return '';
  if (!/^\d{4}-\d{2}$/.test(v)) return '';
  if (!mesIsoValido(v)) return v;
  const d = new Date(`${v}-15T12:00:00`);
  if (!valida(d)) return v;
  return new Intl.DateTimeFormat(loc(), { month: '2-digit', year: 'numeric' }).format(d);
}

/** Fase 2 (W33): nome do mês (1 a 12) por extenso no idioma, com a inicial maiúscula ("Outubro", "October", "Octubre"). */
export function nomeDoMes(mes: number) {
  const nome = new Intl.DateTimeFormat(loc(), { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, mes - 1, 15)));
  return nome.charAt(0).toLocaleUpperCase(loc()) + nome.slice(1);
}

/** "09:30" (valor de input time) no formato de hora do idioma. */
export function horaDeInput(v: string | null | undefined) {
  if (!v || !/^\d{2}:\d{2}/.test(v)) return '';
  const d = new Date(`2000-01-01T${v.slice(0, 5)}:00`);
  if (!valida(d)) return v;
  return new Intl.DateTimeFormat(loc(), { hour: '2-digit', minute: '2-digit' }).format(d);
}

export function hora(v: string | Date) {
  const d = new Date(v);
  if (!valida(d)) return cru(v);
  return new Intl.DateTimeFormat(loc(), { hour: '2-digit', minute: '2-digit' }).format(d);
}

export function relativo(v: string | Date) {
  if (!valida(new Date(v))) return cru(v);
  const s = Math.round((new Date(v).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(loc(), { numeric: 'auto' });
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}

/** Separador decimal do idioma atual ("," em pt e es, "." em en). */
function separadorDecimal() {
  return new Intl.NumberFormat(loc()).formatToParts(1.1).find((p) => p.type === 'decimal')?.value ?? ',';
}

/**
 * Texto digitado para centavos inteiros, pelo separador do idioma atual:
 * pt "1.234,56" e en "1,234.56" viram 123456. Sem separador decimal, é número inteiro de reais.
 */
export function paraCentavos(texto: string): number | null {
  const dec = separadorDecimal();
  const t = texto.trim().replace(/[^\d,.-]/g, '');
  if (!t) return null;
  const negativo = t.startsWith('-');
  const semSinal = t.replace(/-/g, '');
  const i = semSinal.lastIndexOf(dec);
  const inteira = (i >= 0 ? semSinal.slice(0, i) : semSinal).replace(/[.,]/g, '');
  const frac = i >= 0 ? semSinal.slice(i + 1).replace(/[.,]/g, '') : '';
  if (!inteira && !frac) return null;
  const centavos = Number(inteira || '0') * 100 + Math.round(Number(`0.${frac || '0'}`) * 100);
  return Number.isFinite(centavos) ? (negativo ? -centavos : centavos) : null;
}

/**
 * Número digitado (quantidade, percentual, preço em reais) pelo separador do idioma atual: pt "1.234,5" e en "1,234.5"
 * viram 1234.5. Vazio ou sem dígito volta null (review rcrm3 ROB-B4).
 */
export function paraNumero(texto: string): number | null {
  const dec = separadorDecimal();
  const t = texto.trim().replace(/[^\d,.-]/g, '');
  if (!/\d/.test(t)) return null;
  const negativo = t.startsWith('-');
  const semSinal = t.replace(/-/g, '');
  const i = semSinal.lastIndexOf(dec);
  const inteira = (i >= 0 ? semSinal.slice(0, i) : semSinal).replace(/[.,]/g, '');
  const frac = i >= 0 ? semSinal.slice(i + 1).replace(/[.,]/g, '') : '';
  const n = Number(`${inteira || '0'}.${frac || '0'}`);
  return Number.isFinite(n) ? (negativo ? -n : n) : null;
}

/** Número para o campo de edição: separador decimal do idioma, sem milhar (o `paraNumero` lê de volta igual). */
export function numeroEditavel(n: number, casas = 0) {
  return new Intl.NumberFormat(loc(), { minimumFractionDigits: casas, maximumFractionDigits: Math.max(casas, 6), useGrouping: false }).format(n);
}

export function deCentavos(c: number | null | undefined) {
  if (c === null || c === undefined) return '';
  return new Intl.NumberFormat(loc(), { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' }).format(c / 100);
}

/** Dia de hoje no fuso local (aaaa-mm-dd), não em UTC: à noite no Brasil o UTC já virou o dia seguinte. */
export const hojeIso = (agora: Date = new Date()) => `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;

/** CPF ou CNPJ com máscara, só quando os dígitos fecham o tamanho. No modo discreto, mascarado em parte (`•••.456.789-••`). */
export function documentoFormatado(d: string | null | undefined) {
  if (!d) return '';
  const so = d.replace(/\D/g, '');
  let f = d;
  if (/^\d+$/.test(d) && d.length === 11) f = `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  else if (/^\d+$/.test(d) && d.length === 14) f = `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  // Fase 2 (W5): o registro no país de origem pode não ter dígito ("AB-CDEF"); no modo discreto ele some inteiro.
  if (!discretoAtivo()) return f;
  return so ? mascararDocumento(f) : OCULTO;
}

/**
 * Lote 19/10: documento de qualquer país para leitura. No Brasil, como `documentoFormatado` (CPF ou CNPJ, sem rótulo); fora,
 * com o rótulo do tipo ("EIN 12-3456789", "NIF B12345674", "NIPC 509876543"). No modo discreto, o número some como o de antes.
 */
export function documentoDoPais(pais: string | null | undefined, tipo: string | null | undefined, d: string | null | undefined) {
  if (!d) return '';
  if (!pais || pais.toUpperCase() === 'BR') return documentoFormatado(d);
  const f = ehTipoDocumentoPais(tipo) ? formatarDocumentoPais(tipo, d) : d;
  const corpo = !discretoAtivo() ? f : /\d/.test(f) ? mascararDocumento(f) : OCULTO;
  const rotulo = ehTipoDocumentoPais(tipo) ? i18n.t(`documento.tipos.${tipo}`) : (tipo ?? '').trim();
  return rotulo ? `${rotulo} ${corpo}` : corpo;
}

/** Telefone "+55DDNNNNNNNNN" como "(DD) NNNNN-NNNN". No modo discreto, só os 4 últimos dígitos ficam visíveis. */
/**
 * Fase 2 (W19, K5): telefone brasileiro enquanto digita, "(11) 98765-4321" ou "(11) 3322-4455". Com "+" no começo (número de
 * fora), só tira o que não é dígito. O servidor grava em E.164 (+55 quando vem sem DDI).
 */
export function mascaraTelefone(v: string): string {
  if (v.trimStart().startsWith('+')) return `+${v.replace(/\D/g, '').slice(0, 15)}`;
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  const ddd = `(${d.slice(0, 2)}) `;
  const resto = d.slice(2);
  const corte = resto.length > 8 ? 5 : 4;
  return resto.length > corte ? `${ddd}${resto.slice(0, corte)}-${resto.slice(corte)}` : `${ddd}${resto}`;
}

/**
 * Lote 19/10 (item 23): máscara do campo de telefone do formulário enquanto digita. Até 11 dígitos sem "+", a máscara
 * brasileira acima; com mais dígitos (DDI ou zero do DDD digitados sem "+"), fica como veio, para não cortar o número: o
 * servidor normaliza para E.164 do mesmo jeito.
 */
export function mascaraTelefoneDigitando(v: string): string {
  if (!v.trimStart().startsWith('+') && v.replace(/\D/g, '').length > 11) return v;
  return mascaraTelefone(v);
}

export function telefoneFormatado(t: string | null | undefined) {
  if (!t) return '';
  const m = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(t);
  const f = m ? `(${m[1]}) ${m[2]}-${m[3]}` : t;
  return discretoAtivo() ? mascararTelefone(f) : f;
}

/** E-mail como veio; no modo discreto, `g•••@d•••.com`. */
export function email(v: string | null | undefined) {
  if (!v) return '';
  return discretoAtivo() ? mascararEmail(v) : v;
}

/** Rodada rval1-2 (VIS-B2): nome de usuário (login) como veio; no modo discreto, `v•••` (como o e-mail). */
export function nomeDeUsuario(v: string | null | undefined) {
  if (!v) return '';
  return discretoAtivo() ? mascararUsuario(v) : v;
}

/** Contato livre (canal externo): e-mail ou telefone passam pelos formatadores acima; outro texto (perfil, @) volta como veio. */
export function contato(v: string | null | undefined) {
  if (!v) return '';
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())) return email(v.trim());
  if (/^\+?[\d\s().-]{8,}$/.test(v.trim())) return telefoneFormatado(v.trim());
  return v;
}

/**
 * Texto montado pelo servidor ou digitado por alguém (mensagem de sistema do chatter, descrição gerada, observação, descrição
 * da fatura, corpo de mensagem): no modo discreto, os PADRÕES reconhecíveis dentro da frase saem mascarados como nos
 * formatadores acima: valor em dinheiro (símbolo ou código ISO antes ou depois do número, com ou sem milhar, negativo), CPF e
 * CNPJ (com ou sem pontuação), e-mail, telefone (com DDD, com ou sem +55) e CEP. Palavras comuns não são varridas (limite
 * consciente: um nome escrito à mão só some com `textoSemNomes`). Desligado, volta como veio.
 */
export function censurarTexto(texto: string) {
  if (!texto || !discretoAtivo()) return texto;
  if (texto.length <= BLOCO_CENSURA) return aplicarPadroes(texto);
  // Texto grande (mensagem longa, JSON da Auditoria) vai em blocos, defesa extra contra custo superlinear. O corte cai logo
  // depois de um caractere que nenhum padrão usa nem olha em volta (`;`, `!`, `?`, aspas, chaves...; o `:` não, review r14): nenhum padrão é
  // partido e o resultado é idêntico ao do texto inteiro. Sem caractere assim, o bloco cresce até achar um.
  let saida = '';
  let inicio = 0;
  while (inicio < texto.length) {
    NEUTRO.lastIndex = inicio + BLOCO_CENSURA;
    const m = inicio + BLOCO_CENSURA < texto.length ? NEUTRO.exec(texto) : null;
    const fim = m ? m.index + 1 : texto.length;
    saida += aplicarPadroes(texto.slice(inicio, fim));
    inicio = fim;
  }
  return saida;
}

/**
 * Campo de texto livre pelo nome (descrição, observação, título, motivo, nota, assunto, mensagem): nunca é tratado como
 * documento, e-mail ou telefone, mesmo que o nome termine assim (`descricao_documento`, r11 visual M4). Passa por
 * `textoLivre`/`censurarTexto` (ver `tests/r11-texto-livre.test.ts`).
 */
export const campoDeTextoLivre = (nome: string) => /(^|[_.])(descricao|observac(ao|oes)|titulo(?!_eleitor)|motivo|nota|notas|assunto|mensagem|corpo|conteudo|comentario|justificativa)(_|$)/i.test(nome);

export const BLOCO_CENSURA = 16_384;
/**
 * Caractere de corte dos blocos: nenhum padrão o consome nem olha para ele. Review r14 (robustez B1, visual B1): o `:` saiu,
 * porque o CEP sem hífen olha "CEP:" para trás; com o corte no `:`, o bloco seguinte começava em " 01310930" e o CEP passava.
 * Review r15 spec B2: o `=` saiu pelo mesmo motivo ("CEP=04538133").
 */
const NEUTRO = /[;!?"'<>[\]{}|#&*~^`]/g;

/**
 * Moeda: símbolo ou código ISO. O código não pode estar colado a outra letra ("XBRL", "EURos"), mas pode estar colado ao
 * número ("BRL1.234,56", review r12 visual B5).
 */
const MOEDA = String.raw`R\$|US\$|€|£|\$|(?<![A-Za-z])(?:BRL|USD|EUR|GBP)(?![A-Za-z])`;
/** Sinal de negativo: hífen ou o sinal de menos do Unicode (U+2212, review r12 visual B5). */
const MENOS = String.raw`[\-\u2212]`;
/**
 * Número de dinheiro: com grupos de milhar (ponto, vírgula ou espaço, até 6 grupos) ou sem eles (até 15 dígitos), e centavos
 * opcionais com vírgula ou ponto. Todas as repetições têm teto.
 */
const NUMERO = String.raw`(?:\d{1,3}(?:[.,\s]\d{3}){1,6}|\d{1,15})(?:[.,]\d{1,2})?`;
/** Fronteira de número: nada de letra, dígito, ponto, vírgula ou hífen colado antes (não é pedaço de código, data ou valor). */
const ANTES = String.raw`(?<![\w.,\-\u2212])`;
const DEPOIS = String.raw`(?![\w\-\u2212])`;
/**
 * Fronteira de documento, telefone e CEP (review r14 spec M1): vírgula e ponto e vírgula também separam, porque é assim que
 * se cola uma lista de planilha ou CSV ("529.982.247-25,111.444.777-35", "98765-4321;91234-5678"). Continua sem letra,
 * dígito, ponto ou hífen colado antes. A fronteira estrita (`ANTES`) fica só para o dinheiro, que precisa dela contra
 * "1.234,567".
 */
const ANTES_LISTA = String.raw`(?<![\w.\-\u2212])`;
/**
 * Hífen ou ponto colado antes do formato, quando antes dele vem letra, espaço ou pontuação, nunca dígito (review r14 spec M1:
 * "whats-98765-4321", "Dados:-529.982.247-25", "ficha.529.982.247-25"). Só vale para o formato com separador interno: um
 * bloco contíguo depois de "FAT-" é código ("FAT-00000000123"), não documento. Depois de dígito ("10-01310-100") fica de
 * fora (limite declarado). Review r15 spec B2: o sublinhado e o sinal de menos do Unicode também colam ("cpf_529.982.247-25",
 * "saldo\u2212529.982.247-25").
 */
/**
 * Rótulos de contato e de documento que, colados no número, dizem o que ele é ("CPF529.982.247-25", "cel98765-4321",
 * "CNPJ-12.ABC.345/01DE-35"; review r15 visual B3 e B1). Em minúsculas, com a inicial maiúscula ou todo em maiúsculas.
 */
const ROTULOS_CONTATO = ['cpf', 'cnpj', 'cep', 'rg', 'cel', 'celular', 'tel', 'telefone', 'fone', 'whats', 'whatsapp', 'zap'].flatMap((r) => [r, r[0]!.toUpperCase() + r.slice(1), r.toUpperCase()]);
const ROTULO_CONTATO = String.raw`(?<![A-Za-z])(?:${ROTULOS_CONTATO.join('|')})`;
/**
 * Review r15 visual B1: a letra antes do hífen ou do ponto colado tem de ser minúscula (palavra: "whats-98765-4321",
 * "ficha.529.982.247-25", "cpf_529.982.247-25") ou o rótulo de contato ("CNPJ-12.ABC...", "TEL-98765-4321"); sigla em
 * maiúsculas é código ("SKU AB-3456-7890", "ref ABC-2345-6789", "PRD.3456-7890", "X.98765-4321") e fica de fora.
 */
const ANTES_COLADO = String.raw`(?<=(?:^|[^\d.,\-\u2212_A-Za-z]|[a-z\u00DF-\u00FF]|${ROTULO_CONTATO})[\-._\u2212])`;
/** O rótulo de contato colado direto no número, sem separador ("CPF529.982.247-25", "cel98765-4321"; review r15 visual B3). */
const ANTES_ROTULO = String.raw`(?<=${ROTULO_CONTATO})`;
/** Colado em hífen ou ponto depois de palavra, ou direto no rótulo de contato. */
const ANTES_COLADO_OU_ROTULO = String.raw`(?:${ANTES_COLADO}|${ANTES_ROTULO})`;
/** Uma das fronteiras acima (formato com separador interno). */
const ANTES_SEP = String.raw`(?:${ANTES_LISTA}|${ANTES_COLADO_OU_ROTULO})`;
/**
 * Fronteira do CEP com hífen: aceita também a sigla em maiúsculas antes do hífen ou do ponto, porque a UF vem assim
 * ("SP-01.310-100", review r14); o formato do CEP não se confunde com o do telefone num código.
 */
const ANTES_SEP_CEP = String.raw`(?:${ANTES_LISTA}|(?<=(?:^|[^\d.,\-\u2212_])[\-._\u2212])|${ANTES_ROTULO})`;
/**
 * Quantidade depois do número (review r14 visual B3): "3000-4000 unidades" é intervalo, não o fixo "3000-4000". Unidade ou
 * palavra de quantidade (pt, en, es) depois, ou palavra de intervalo antes ("faixa", "entre"), tira o fixo sem DDD da censura.
 */
const QUANTIDADE_DEPOIS = String.raw`(?!\s{0,2}(?:unidades?|unid|und|un|p[çc]s?|pe[çc]as?|itens?|kg|g|t|ton|m|m2|m3|cm|mm|km|l|ml|h|hs|horas?|min|minutos?|dias?|semanas?|meses|anos|pessoas|caixas?|pacotes?|metros?|litros?|quilos?|toneladas?|reais|vezes|pontos|units?|items?|pcs|pieces|days|hours|weeks|months|years|people|boxes|unidad|art[íi]culos?|personas|cajas|piezas|%)(?![A-Za-zÀ-ÿ0-9]))`;
const QUANTIDADE_ANTES = String.raw`(?<!(?:faixa|entre|intervalo|quantidade|qtd|qtde|range|between|rango)\s{1,2})`;
/**
 * Separador de bloco de telefone: hífen ou espaço (review r12 visual M6). Review r21 visual (B3): também o travessão curto
 * (U+2013) e o longo (U+2014), que o corretor do editor põe no lugar do hífen ("(11) 98765\u20134321").
 */
const TRACO = String.raw`[\-\u2013\u2014]`;
const SEP = String.raw`[\-\s\u2013\u2014]`;
/**
 * Telefone BR (review r12 visual M6 e spec M1), com teto em tudo e começo ancorado (no "+", no "(" ou pela fronteira):
 *  - "+55 11 98765-4321", "+55 11 98765 4321", "+55 (11) 98765-4321", "+5511987654321";
 *  - "(11) 98765-4321", "(11)98765 4321";
 *  - "11 98765-4321", "11 98765 4321", "11-98765-4321", "1198765-4321", "11 3456-7890";
 *  - sem DDD, só celular (começa com 9): "98765-4321", "98765 4321", "987654321";
 *  - fixo com DDD contíguo, 10 dígitos: "1134567890". Com 11 dígitos contíguos, quem casa é o padrão de CPF; review r14
 *    spec B2: DDD válido seguido de 9 ("11987654321") é celular e sai pela máscara do telefone (só os 4 últimos à vista).
 * Review r13 (visual M4, spec B2): o 9 do celular separado por espaço ou hífen ("(11) 9 8765-4321", "+55 11 9 8765-4321",
 * "9 8765-4321") e o fixo sem DDD com hífen ("3456-7890", começando de 2 a 5, como o fixo brasileiro). O fixo sem DDD só com
 * hífen, e nunca com cara de ano ("2026-0042"): "3456 7890" e "34567890" ficam de fora para não varrer número de nota,
 * pedido ou quantidade (limite declarado).
 * Review r14 (spec M1, visual B3): vírgula, ponto e vírgula e o hífen ou ponto colado depois de letra também são fronteira
 * (`ANTES_LISTA`, `ANTES_SEP`); o fixo sem DDD seguido de unidade ou depois de "faixa"/"entre" é intervalo, não telefone.
 */
const NOVE = String.raw`(?:9${SEP}?)?`;
const TELEFONE = [
  String.raw`\+55${SEP}?\(?\d{2}\)?${SEP}?${NOVE}\d{4}${SEP}?\d{4}`,
  String.raw`\(\d{2}\)${SEP}?${NOVE}\d{4}${SEP}?\d{4}`,
  String.raw`${ANTES_SEP}\d{2}${SEP}${NOVE}\d{4}${SEP}?\d{4}${DEPOIS}`,
  String.raw`${ANTES_SEP}\d{2}9?\d{4}${TRACO}\d{4}${DEPOIS}`,
  String.raw`(?:${ANTES_LISTA}|${ANTES_ROTULO})9${SEP}?\d{4}${SEP}?\d{4}${DEPOIS}`,
  String.raw`${ANTES_COLADO}9(?:${SEP}\d{4}${SEP}?|\d{4}${SEP})\d{4}${DEPOIS}`,
  // Review r15 visual B3: celular de 11 dígitos contíguos com DDD colado em hífen depois de palavra ou no rótulo ("cel-11987654321").
  String.raw`${ANTES_COLADO_OU_ROTULO}(?:1[1-9]|2[1247-8]|3[1-578]|4[1-9]|5[1345]|6[1-9]|7[134579]|8[1-9]|9[1-9])9\d{8}${DEPOIS}`,
  String.raw`${ANTES_SEP}${QUANTIDADE_ANTES}(?!(?:19|20)\d{2}-)[2-5]\d{3}-\d{4}${DEPOIS}${QUANTIDADE_DEPOIS}`,
  String.raw`${ANTES_LISTA}[1-9]{2}[2-9]\d{7}${DEPOIS}`,
  // Review r21 spec (M2): o número com o 55 e sem o "+", contíguo, como o WhatsApp manda ("5521988887777", celular com 13
  // dígitos; "552134567890", fixo com 12): DDD válido e o 9 do celular ou o fixo começando de 2 a 5. Exatamente 12 ou 13
  // dígitos (a fronteira não deixa casar pedaço de um id ou de um instante maior).
  String.raw`${ANTES_LISTA}55(?:1[1-9]|2[1247-8]|3[1-578]|4[1-9]|5[1345]|6[1-9]|7[134579]|8[1-9]|9[1-9])(?:9\d{8}|[2-5]\d{7})${DEPOIS}`,
].join('|');
/**
 * CPF e CNPJ com pontuação, só dígitos, com espaços no lugar da pontuação ou mistos ("123 456 789 09", "529982247-25",
 * "11444777/0001-61"; review r12 visual B5 e spec M1). Review r13 visual B5: qualquer separador de ponto, hífen, espaço ou
 * barra entre os blocos ("529.982.247.25", "529-982-247-25", "11.444.777.0001-61", "11-444-777/0001-61").
 */
/** Review r14 visual B4: o travessão curto (U+2013, "529.982.247\u201325") também separa; review r21 visual B3, o longo (U+2014) também. */
const SEP_DOC = String.raw`[.\s\-/\u2013\u2014]?`;
const CPF = String.raw`\d{3}${SEP_DOC}\d{3}${SEP_DOC}\d{3}${SEP_DOC}\d{2}`;
const CNPJ = String.raw`\d{2}${SEP_DOC}\d{3}${SEP_DOC}\d{3}${SEP_DOC}\d{4}${SEP_DOC}\d{2}`;
/**
 * Documento (review r14 spec M1): na fronteira de lista (vírgula, ponto e vírgula) em qualquer forma; colado depois de hífen ou
 * ponto só com separador logo depois do primeiro bloco ("Dados:-529.982.247-25"), nunca o bloco contíguo ("FAT-00000000123").
 */
const DOCUMENTO = String.raw`(?:${ANTES_LISTA}(?:${CNPJ}|${CPF})|${ANTES_COLADO_OU_ROTULO}(?=\d{2,3}[.\s\-/\u2013\u2014])(?:${CNPJ}|${CPF}))${DEPOIS}`;
/**
 * CNPJ alfanumérico (emitido desde julho de 2026; review r14 visual B4) em texto livre, só na forma pontuada completa
 * ("12.ABC.345/01DE-35", com ao menos uma letra): sem a pontuação, 14 letras e dígitos são código de produto ou de pedido.
 * Review r15 spec M2: colado em hífen ou ponto depois de letra (`ANTES_SEP`, como o numérico: "CNPJ-12.ABC.345/01DE-35",
 * "matriz.12.ABC.345/01DE-35"); spec B2: em minúsculas também (a classe tem as duas caixas; sem o flag `i`, que faria a
 * sigla em maiúsculas contar como palavra na fronteira colada).
 */
const CNPJ_ALFA = String.raw`${ANTES_SEP}(?=[A-Za-z\d.\/]{0,15}[A-Za-z])[A-Za-z\d]{2}\.[A-Za-z\d]{3}\.[A-Za-z\d]{3}\/[A-Za-z\d]{4}-\d{2}${DEPOIS}`;
/** DDD brasileiro válido: 11 dígitos contíguos com DDD válido e o 9 do celular são telefone, não CPF (review r14 spec B2). */
const DDD = String.raw`(?:1[1-9]|2[1247-8]|3[1-578]|4[1-9]|5[1345]|6[1-9]|7[134579]|8[1-9]|9[1-9])`;
const CELULAR_CONTIGUO = new RegExp(String.raw`^${DDD}9\d{8}$`);

/**
 * Review r22 segurança (sondagem do modo discreto): telefone enviado como NÚMERO, e não como texto (`{"contato":
 * 5521988887777}`), passava cru no JSON. Valem os mesmos formatos que a censura de texto reconhece contíguos: com ou sem o 55,
 * DDD válido e o 9 do celular (11 ou 13 dígitos) ou o fixo de 2 a 5 (10 ou 12 dígitos). Fora disso (instante, valor, id de
 * outro tamanho, DDD inválido), o número fica.
 */
const TELEFONE_NUMERICO = new RegExp(String.raw`^(?:55)?${DDD}(?:9\d{8}|[2-5]\d{7})$`);
/**
 * Chaves cujo número é id, instante ou versão (o instante em segundos, "1759700000", tem 10 dígitos e começa com um DDD
 * válido): nelas o número nunca é tratado como telefone.
 */
const CHAVE_NAO_TELEFONE = /(^|[_.-])(id|ids|uid|em|at|ts|ms|seq|epoch|hora|data|instante|prazo|versao|version)$|timestamp|time|date|expira|created|updated|criad|atualizad|enviad|recebid/i;
const CHAVE_NAO_TELEFONE_CAMEL = /[a-z](Id|Ids|At|Ts|Ms|Seq)$/;

/** O número desta chave é um telefone brasileiro (contíguo, com ou sem o 55)? Ver `TELEFONE_NUMERICO`. */
export function telefoneNumerico(chave: string, v: number): boolean {
  if (!Number.isSafeInteger(v) || v <= 0) return false;
  if (CHAVE_NAO_TELEFONE.test(chave) || CHAVE_NAO_TELEFONE_CAMEL.test(chave)) return false;
  return TELEFONE_NUMERICO.test(String(v));
}

/** Máscara do CNPJ alfanumérico: como o numérico, os 2 primeiros e os 2 últimos caracteres somem, o miolo fica. */
function mascararCnpjAlfa(s: string) {
  let i = 0;
  return s.replace(/[A-Za-z\d]/g, (c) => (i++ >= 2 && i <= 12 ? c : '•'));
}
/**
 * E-mail ASCII e Unicode (review r12 spec M1): parte local com letras, números e marcas de qualquer escrita ("joão",
 * "maria.joão"), rótulos do domínio com letras Unicode e sublinhado ("café", "minha_empresa") e o final só com letras.
 * Mesmos tetos de antes: parte local até 64, rótulos até 63, até 8 subdomínios, final de 2 a 24.
 */
const LOCAL = String.raw`[\p{L}\p{M}\p{N}._%+\-]`;
const ROTULO = String.raw`[\p{L}\p{M}\p{N}_\-]{1,63}`;
// Review r14 visual B4: até 30 subdomínios (com 8, "joao@a.b.c.d.e.f.g.h.i.j.empresa.com.br" não casava e saía inteiro). O
// custo por começo de sequência continua constante (o rótulo não tem ponto, então só a contagem recua).
const EMAIL = String.raw`(?<!${LOCAL})${LOCAL}{1,64}@${ROTULO}(?:\.${ROTULO}){0,30}\.[\p{L}\p{M}]{2,24}`;

/**
 * Padrões da censura, em ordem. Review r11 robustez (M1): toda repetição que pode recomeçar em qualquer posição tem teto, e o
 * começo das sequências é ancorado (no símbolo da moeda ou por lookbehind no início do número ou do e-mail), então cada posição
 * custa tempo constante e o texto inteiro, tempo linear. Antes, 50 KB sem espaço levavam 5 s: a regex de e-mail recomeçava em
 * cada letra e varria o resto; a de valor com o símbolo depois, em cada grupo de "1.234.234...".
 * Review r11 visual (M3): valor com o símbolo depois e sem milhar ("4321,98 R$", "1234,56 €", "500 €") e código ISO
 * ("BRL 1.234,56", "1.234,56 EUR") também somem; o sinal de negativo some junto (a marca não diz se é negativo).
 * E-mail: parte local de até 64 caracteres a partir do começo da sequência, rótulos de até 63, até 8 subdomínios (parte local
 * acima de 64 é inválida pela RFC 5321 e não é reconhecida).
 * Review r12 (visual M6 e B5, spec M1): telefone com espaço, contíguo ou sem DDD, CEP sem hífen, CPF e CNPJ com espaços, e-mail
 * Unicode e com sublinhado no domínio, sinal de menos do Unicode e código ISO colado ao número. A tabela de formatos fica em
 * `tests/r12-censura.test.ts`. Formato fora da lista, em texto livre, é limite declarado (régua de severidade, CONTRATOS §6).
 */
const PADROES: [RegExp, (m: RegExpExecArray) => string][] = [
  // Símbolo antes: "R$ 1.234,56", "-R$ 10,00", "R$ -10", "R$ −10,00", "US$1,234.56", "BRL 1.234,56", "BRL1.234,56", "€ 1 234,56",
  // e o negativo entre parênteses depois do símbolo, "R$ (1.234,56)" (review r13 visual B5; o parêntese fica); review r14
  // visual B4: só centavos sem o zero, "R$ .99". Review r15 spec B1: o sinal antes do parêntese também, "R$ -(1.234,56)"; spec
  // O1: o hífen colado depois de palavra fica ("valor-R$ 100,00" vira "valor-R$ •••••"), só o sinal solto some junto.
  [new RegExp(String.raw`(?:(?<![A-Za-z\u00C0-\u024F\d])${MENOS})?(${MOEDA})[\s]?${MENOS}?(\()?${MENOS}?[\s]?(?:[.,](?=\d))?\d(?:(?:[\d.,]|\s(?=\d{3}(?!\d)))*\d)?`, 'g'), (m) => `${m[1]}\u00a0${m[2] ?? ''}${OCULTO}`],
  // Símbolo depois: "1.234,56 R$", "4321,98 R$", "-1.234,56 €", "−1.234,56 €", "500 €", "1,234.56 USD".
  // E o negativo entre par\u00eanteses com o s\u00edmbolo depois, "(1.234,56) R$" (review r14 visual B4; os par\u00eanteses ficam).
  // Review r15 visual B2: espaço dentro dos parênteses ("( 1.234,56 ) R$") e só centavos sem o zero (".99 R$", ",99 €").
  [new RegExp(String.raw`${ANTES}(\(\s?)?${MENOS}?(?:${NUMERO}|[.,]\d{1,2})(\s?\))?\s?(${MOEDA})`, 'g'), (m) => `${m[1] ?? ''}${OCULTO}${m[2] ?? ''}\u00a0${m[3]}`],
  // CPF e CNPJ, com ou sem pontuação, ou com espaços (nunca pedaço de código como FAT-00000000123).
  // Review r14 spec B2: 11 dígitos contíguos com DDD válido e o 9 do celular saem como telefone (4 últimos à vista).
  [new RegExp(DOCUMENTO, 'g'), (m) => (CELULAR_CONTIGUO.test(m[0]) ? mascararTelefone(m[0]) : mascararDocumento(m[0]))],
  [new RegExp(CNPJ_ALFA, 'g'), (m) => mascararCnpjAlfa(m[0])],
  [new RegExp(EMAIL, 'gu'), (m) => mascararEmail(m[0])],
  [new RegExp(TELEFONE, 'g'), (m) => mascararTelefone(m[0])],
  // CEP com hífen ou ponto: "01310-100", "01.310-100". Sem hífen, só com "CEP" antes ("CEP 04538133", "cep: 01310 930"): 8
  // dígitos soltos são número de nota, pedido ou protocolo (review r13 visual B5, falso positivo). Review r14: fronteira de
  // lista e hífen colado depois de letra (spec M1); o rótulo aceita "CEP nº", "CEP -", "C.E.P." e até 2 espaços (visual B4).
  // Review r15 spec B2: "CEP n.º", "CEP=" e até 8 espaços.
  // Review r15 visual B3: "CEP: nº", "CEP n.", "CEP" seguido do travessão curto (U+2013). Sem o flag `i` (a fronteira colada distingue a caixa):
  // o rótulo aceita "CEP", "cep", "Cep" e "C.E.P.".
  // Review r21 visual (B3): o travessão curto ou longo no lugar do hífen ("01310\u2013100") e depois do rótulo ("CEP \u2014 04538133").
  [new RegExp(String.raw`${ANTES_SEP_CEP}(?:\d{5}|\d{2}\.\d{3})${TRACO}\d{3}${DEPOIS}|(?<=(?<![\p{L}\p{N}])(?:[Cc]\.?[Ee]\.?[Pp]\.?)(?:\s{0,8}[:=\-\u2013\u2014])?\s{0,8}(?:[nN]\.?[º°oO]?\.?\s{0,8})?)\d{5}\s?\d{3}(?![\w\-\u2212])`, 'gu'), () => '•••••-•••'],
];

/** Os padrões, para a prova de tempo e de backtracking (`tests/r11-censura.test.ts`). */
export const PADROES_CENSURA: readonly RegExp[] = PADROES.map(([re]) => re);

function aplicarPadroes(texto: string): string {
  let r = texto;
  for (const [re, fn] of PADROES) {
    re.lastIndex = 0;
    r = r.replace(re, (...a) => fn(a.slice(0, -2) as unknown as RegExpExecArray));
  }
  return r;
}

const escaparRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Nomes procurados no texto: sem repetição, com 4 letras ou mais, do mais longo ao mais curto (evita trocar pedaço de palavra). */
const nomesParaTrocar = (nomes: (string | null | undefined)[]) => [...new Set(nomes.map((x) => x?.trim() ?? '').filter((x) => x.length >= 4))].sort((a, b) => b.length - a.length);

/**
 * Texto livre exibido (observação, descrição, corpo de mensagem, motivo): no modo discreto, os nomes conhecidos viram
 * iniciais (com a opção de nomes) e os padrões reconhecíveis somem (`censurarTexto`). Desligado, volta como veio.
 */
export function textoLivre(texto: string | null | undefined, ...nomes: (string | null | undefined)[]) {
  if (!texto) return '';
  return censurarTexto(textoSemNomes(texto, ...nomes));
}

/** Forma de slug de um nome, a mesma do `slugificar` do servidor ("Alexandre Nogueira Reis" vira "alexandre-nogueira-reis"). */
export const slugDeNome = (n: string) =>
  n
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/**
 * Nome de arquivo (anexo do chatter, PDF gerado pelo sistema; review r12 visual M2): como o texto livre (`textoLivre`), e com a
 * opção de nomes também os nomes conhecidos na forma de slug, com hífen ou sublinhado ("holerite-2026-09-alexandre-nogueira-reis.pdf"
 * vira "holerite-2026-09-A.N.R.pdf"). Review r13 visual B4: a sigla no slug vai sem o ponto final, e a sigla do nome por extenso que
 * encosta na extensão perde o ponto repetido ("Contrato A. N. R..pdf" vira "Contrato A. N. R.pdf").
 */
export function nomeDeArquivo(nome: string | null | undefined, ...nomes: (string | null | undefined)[]) {
  if (!nome) return '';
  let r = nome;
  if (nomesOcultos()) {
    for (const n of nomesParaTrocar(nomes)) {
      const slug = slugDeNome(n);
      if (slug.length < 4) continue;
      const sigla = iniciais(n).replace(/\s+/g, '').replace(/\.$/, '');
      for (const v of new Set([slug, slug.replace(/-/g, '_')])) r = r.replace(new RegExp(escaparRegex(v), 'gi'), () => sigla);
    }
  }
  const t = textoLivre(r, ...nomes);
  return nomesOcultos() ? t.replace(/(\p{Lu})\.\.(?=[^.\s]*$)/u, '$1.') : t;
}

/** Chave Pix: CPF/CNPJ, e-mail e telefone pelos formatadores acima; chave aleatória vira a marca no modo discreto. */
export function chavePix(v: string | null | undefined) {
  if (!v) return '';
  const t = v.trim();
  if (/^\d{11}$|^\d{14}$|^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(t)) return documentoFormatado(t.replace(/\D/g, ''));
  const c = contato(t);
  if (c !== t || !discretoAtivo()) return c;
  return OCULTO;
}

/** Agência: oculta no modo discreto. */
export const agenciaBancaria = (v: string | null | undefined) => (!v ? '' : discretoAtivo() ? OCULTO : v);
/** Número de conta: no modo discreto, só os 2 últimos dígitos. */
export const numeroConta = (v: string | null | undefined) => (!v ? '' : discretoAtivo() ? v.replace(/\d(?=(?:\D*\d){2})/g, '•') : v);

/** Conta bancária numa linha ("Banco / ag 1234 / cc 56789-0 / Pix ..."); no modo discreto, agência oculta e da conta só os 2 últimos dígitos. */
export function contaBancaria(c: { banco?: string | null; agencia?: string | null; conta?: string | null; chave_pix?: string | null }) {
  const agencia = c.agencia ? `ag ${agenciaBancaria(c.agencia)}` : '';
  const conta = c.conta ? `cc ${numeroConta(c.conta)}` : '';
  return [c.banco, agencia, conta, c.chave_pix ? `Pix ${chavePix(c.chave_pix)}` : ''].filter(Boolean).join(' / ');
}

export interface EnderecoTexto {
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
}

/** Endereço numa linha; no modo discreto, só a cidade e a UF. */
export function endereco(e: EnderecoTexto | null | undefined) {
  if (!e) return '';
  const cidadeUf = [e.cidade, e.uf].filter(Boolean).join('/');
  if (discretoAtivo()) return cidadeUf;
  return [e.logradouro, e.numero, e.complemento, e.bairro, cidadeUf, e.cep].filter(Boolean).join(', ');
}

/** Nomes de pessoas e empresas escondidos na tela agora (modo discreto com a opção de nomes). */
export const nomesEscondidos = () => nomesOcultos();

/**
 * Review r17 visual B2: nome de fábrica do parceiro "Diversos" (W24), gravado em pt pelo servidor (`NOME_PARCEIRO_DIVERSOS` de
 * `modules/cadastros/src/publico.ts`). Como as categorias de fábrica (`rotuloCategoria`), o nome exato faz as vezes do código e
 * aparece no idioma da pessoa; renomeado, aparece como está.
 */
export const NOME_PARCEIRO_DIVERSOS = 'Diversos';
const nomeDeFabrica = (v: string) => (v === NOME_PARCEIRO_DIVERSOS ? i18n.t('comum.parceiroDiversos', { ns: 'nucleo' }) : v);

/** Nome de pessoa ou empresa; dados sensíveis (inclusive e-mail usado como nome) são mascarados, e a opção de nomes usa iniciais. */
export function nomePessoa(v: string | null | undefined) {
  if (!v) return '';
  const nome = nomeDeFabrica(v);
  return nomesOcultos() ? iniciais(nome) : censurarTexto(nome);
}

/**
 * Fase 2 (W20, mesma classe da review r16 robustez M3): nome do parceiro de um registro visível (agendamento, conversa,
 * contrato, documento, pedido). O servidor manda `null` quando o parceiro é de uma empresa que a pessoa não vê (parceiros
 * separados por empresa): a tela mostra o rótulo neutro "Cadastro de outra empresa" em vez de um vazio. Com nome, é o
 * `nomePessoa` (iniciais no modo discreto com a opção de nomes).
 */
export function nomeParceiro(v: string | null | undefined) {
  if (v === null || v === undefined) return i18n.t('comum.cadastroOutraEmpresa', { ns: 'nucleo' });
  return nomePessoa(v);
}

/**
 * Review r17 robustez (M3, mesma classe): nome de um cadastro que não é pessoa (recurso, serviço) de um registro visível. O
 * servidor manda `null` quando o cadastro ficou numa empresa que a pessoa não vê: o rótulo neutro, como `nomeParceiro`.
 */
export function nomeCadastro(v: string | null | undefined) {
  return v === null || v === undefined ? i18n.t('comum.cadastroOutraEmpresa', { ns: 'nucleo' }) : v;
}

/**
 * Texto livre que pode citar nomes conhecidos ("Implantação de ERP: Ana Souza", "Fatura para Aurora Ltda"): com a opção de
 * nomes, cada nome dado (o do parceiro da linha, do contato) vira as iniciais dentro do texto (r9 visual M2). Sem a opção, volta
 * como veio. Nome com menos de 4 letras não é procurado (evita trocar pedaço de palavra).
 */
export function textoSemNomes(texto: string | null | undefined, ...nomes: (string | null | undefined)[]): string {
  if (!texto) return '';
  if (!nomesOcultos()) return texto;
  let r = texto;
  for (const n of nomesParaTrocar(nomes)) r = r.replace(new RegExp(escaparRegex(n), 'gi'), () => iniciais(n));
  return r;
}
